import { useFormContext as useReactHookFormContext } from 'react-hook-form';
import { z } from 'zod';

import { VatAmountItem } from '__generated__/GQL';
import locales from 'common/zod/locales';

const MAX_VALUE = 100;
const MAX_VALUE_ERROR_MESSAGE = locales.formatString(
  locales.errors.maxValue,
  MAX_VALUE,
) as string;

const MAX_DECIMALS = 2;
const MAX_DECIMALS_ERROR_MESSAGE = locales.formatString(
  locales.errors.maxDecimals,
  MAX_DECIMALS,
) as string;

export const formSchema = z.object({
  rows: z.array(
    z
      .object({
        trueVatAmount: z
          .number({ coerce: true })
          .min(0, locales.errors.positive)
          .nullable(),
        trueVatRate: z
          .number({ coerce: true })
          .step(0.01, MAX_DECIMALS_ERROR_MESSAGE)
          .max(MAX_VALUE, MAX_VALUE_ERROR_MESSAGE)
          .min(0, locales.errors.positive)
          .nullable(),
      })
      .refine(({ trueVatAmount, trueVatRate }) => {
        // a user should not be able to enter a trueVatAmount without a trueVatRate
        if (
          trueVatAmount &&
          (trueVatRate === undefined || trueVatRate === null)
        ) {
          return false;
        }

        // and vice versa
        if (
          trueVatRate &&
          (trueVatAmount === undefined || trueVatAmount === null)
        ) {
          return false;
        }

        return true;
      }),
  ),
});

export type FormSchema = z.infer<typeof formSchema>;
type Row = FormSchema['rows'][number];

export const useFormContext = () => useReactHookFormContext<FormSchema>();

export const emptyRow: Row = {
  trueVatAmount: null,
  trueVatRate: null,
};

type RetrieveVatAmountItem = Pick<
  VatAmountItem,
  'trueVatAmount' | 'trueVatRate' | 'vatAmountItemId'
>;

export const getDefaultRows = (vatAmountItems?: RetrieveVatAmountItem[]) => {
  if (!vatAmountItems || vatAmountItems.length === 0) {
    return [emptyRow];
  }

  return vatAmountItems.map(
    ({ trueVatAmount, trueVatRate, vatAmountItemId }) => ({
      trueVatAmount,
      trueVatRate: trueVatRate !== null ? trueVatRate * 100 : null,
      vatAmountItemId,
    }),
  );
};
